import { useMemo } from "react";
import type { EventId, PlanEvent } from "../../../../shared/types";
import { useFormPreview } from "../../stores/form-preview-store";
import type { FormAnchor } from "../../hooks/use-week-calendar-state";
import {
  HOURS,
  TOTAL_GRID_HEIGHT_PX,
  HOUR_HEIGHT_PX,
  DAY_START_HOUR,
} from "../../utils/time-constants";
import {
  getEventPosition,
  getNowIndicatorOffset,
  computeOverlapColumns,
  pxToTime,
  pxToSnappedPx,
} from "../../utils/time-positioning";
import { minutesToTime } from "../../hooks/drag-drop-helpers";
import { EventBlock } from "./EventBlock";
import { NowIndicator } from "./NowIndicator";
import { AllDayEventBlock } from "./AllDayEventBlock";
import { HoverTimeIndicator } from "./HoverTimeIndicator";
import { SlotCreationPreviewBlock } from "./SlotCreationPreviewBlock";
import { useDayColumnInteraction } from "./useDayColumnInteraction";

interface SlotCreationPreview {
  readonly topPx: number;
  readonly heightPx: number;
}

interface DayColumnProps {
  readonly date: string;
  readonly today: string;
  readonly now: Date;
  readonly events: readonly PlanEvent[];
  readonly linkedCountByEvent?: ReadonlyMap<EventId, number>;
  readonly draggingEventId?: string | null;
  readonly resizingEventId?: string | null;
  readonly resizingHeightPx?: number;
  readonly resizingTopPx?: number;
  readonly resizingStartMinutes?: number;
  readonly resizingEndMinutes?: number;
  readonly resizingEdge?: "top" | "bottom";
  readonly ghostDragEvent?: { startTime: string; endTime: string; eventId: string } | null;
  readonly slotCreationPreview?: SlotCreationPreview | null;
  readonly onEventClick?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onEventLongPress?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onEventContextMenu?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onSlotClick?: (startTime: string, endTime: string, anchor: FormAnchor) => void;
  readonly onSlotCreationMouseDown?: (
    date: string,
    mouseY: number,
    columnEl: HTMLDivElement,
  ) => void;
  readonly onDragStart?: (
    event: PlanEvent,
    mouseYInColumn: number,
    clientX: number,
    clientY: number,
  ) => void;
  readonly onResizeStart?: (
    event: PlanEvent,
    clientY: number,
    edge: "top" | "bottom",
  ) => void;
  readonly onDayContextMenu?: (
    date: string,
    time: string,
    anchorRect: DOMRect,
  ) => void;
}

/** Apply form preview overrides to events on the current day */
function applyFormPreview(
  events: readonly PlanEvent[],
  preview: ReturnType<typeof useFormPreview>,
  date: string,
): readonly PlanEvent[] {
  if (!preview || !preview.eventId) return events;

  // The edited event may have moved to a different day
  const editedOnThisDay = preview.date === date;
  const originalOnThisDay = events.some((e) => e.id === preview.eventId);

  if (!editedOnThisDay && !originalOnThisDay) return events;

  let result = events.filter((e) => e.id !== preview.eventId);

  if (editedOnThisDay && !preview.allDay) {
    const original = events.find((e) => e.id === preview.eventId);
    if (original) {
      result = [
        ...result,
        {
          ...original,
          category: preview.category,
          startTime: preview.startTime,
          endTime: preview.endTime,
          title: preview.title,
          date: preview.date,
          allDay: preview.allDay,
        },
      ];
    }
  }

  return result;
}

export function DayColumn({
  date,
  today,
  now,
  events,
  linkedCountByEvent,
  draggingEventId,
  resizingEventId,
  resizingHeightPx,
  resizingTopPx,
  resizingStartMinutes,
  resizingEndMinutes,
  resizingEdge,
  ghostDragEvent,
  slotCreationPreview,
  onEventClick,
  onEventLongPress,
  onEventContextMenu,
  onSlotClick,
  onSlotCreationMouseDown,
  onDragStart,
  onResizeStart,
  onDayContextMenu,
}: DayColumnProps) {
  const formPreview = useFormPreview();
  const previewedEvents = useMemo(
    () => applyFormPreview(events, formPreview, date),
    [events, formPreview, date],
  );

  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay: PlanEvent[] = [];
    const timed: PlanEvent[] = [];
    for (const e of previewedEvents) {
      if (e.allDay) allDay.push(e);
      else timed.push(e);
    }
    return { allDayEvents: allDay, timedEvents: timed };
  }, [previewedEvents]);

  const eventsWithGhost = useMemo(() => {
    let result = timedEvents as PlanEvent[];

    // Ghost pour la création de créneau
    if (slotCreationPreview && slotCreationPreview.heightPx > 0) {
      const topPx = Math.min(slotCreationPreview.topPx, slotCreationPreview.topPx + slotCreationPreview.heightPx);
      const bottomPx = Math.max(slotCreationPreview.topPx, slotCreationPreview.topPx + slotCreationPreview.heightPx);
      const ghost: PlanEvent = {
        id: "__ghost__" as EventId,
        title: "",
        category: "personal",
        date,
        startTime: pxToTime(topPx, true),
        endTime: pxToTime(bottomPx, true),
        createdAt: 0,
        description: "",
        notes: "",
        tags: [],
        recurrence: null,
        reminderMinutes: null,
        reminderTime: null,
        reminderType: null,
      };
      result = [...result, ghost];
    }

    // Ghost pour le drag d'événement
    if (ghostDragEvent) {
      const ghost: PlanEvent = {
        id: "__ghost_drag__" as EventId,
        title: "",
        category: "personal",
        date,
        startTime: ghostDragEvent.startTime,
        endTime: ghostDragEvent.endTime,
        createdAt: 0,
        description: "",
        notes: "",
        tags: [],
        recurrence: null,
        reminderMinutes: null,
        reminderTime: null,
        reminderType: null,
      };
      // Retirer l'événement original qui est en train d'être dragué
      result = [...result.filter((e) => e.id !== ghostDragEvent.eventId), ghost];
    }

    return result;
  }, [timedEvents, slotCreationPreview, ghostDragEvent, date]);

  const overlapMap = computeOverlapColumns(eventsWithGhost);
  const isToday = date === today;
  const nowOffset = isToday ? getNowIndicatorOffset(now) : null;
  const isDraggingOrResizing = !!draggingEventId || !!resizingEventId;

  const {
    hoverY,
    hoverTime,
    shiftHeld,
    ctrlHeld,
    handleMouseMove,
    handleMouseLeave,
    handleGridMouseDown,
    handleGridTouchStart,
    handleGridClick,
    touchY,
  } = useDayColumnInteraction({
    date,
    isDraggingOrResizing,
    allDayEvents,
    timedEvents,
    onSlotCreationMouseDown,
    onSlotClick,
  });

  return (
    <div
      data-daycol={date}
      className={`relative border-l border-border/20 ${
        isToday ? "bg-accent/[0.03]" : ""
      } ${(shiftHeld || ctrlHeld) ? "[&>button]:pointer-events-none" : ""}`}
      style={{ height: `${TOTAL_GRID_HEIGHT_PX}px` }}
      onMouseDown={handleGridMouseDown}
      onTouchStart={handleGridTouchStart}
      onClick={handleGridClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onContextMenu={(e) => {
        e.preventDefault();
        if (!onDayContextMenu) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const time = pxToTime(pxToSnappedPx(y, e.shiftKey), e.shiftKey);
        onDayContextMenu(date, time, new DOMRect(e.clientX, e.clientY, 0, 0));
      }}
    >
      {HOURS.map((hour) => (
        <div
          key={hour}
          className="absolute left-0 right-0 border-t border-border/10"
          style={{ top: `${(hour - HOURS[0]) * HOUR_HEIGHT_PX}px` }}
        />
      ))}
      {HOURS.map((hour) => (
        <div
          key={`half-${hour}`}
          className="absolute left-0 right-0 border-t border-border/[0.05]"
          style={{
            top: `${(hour - HOURS[0]) * HOUR_HEIGHT_PX + HOUR_HEIGHT_PX / 2}px`,
          }}
        />
      ))}

      {/* All-day events — full-height overlay */}
      {allDayEvents.map((event, i) => (
        <AllDayEventBlock
          key={event.id}
          event={event}
          index={i}
          isPast={date < today}
          onClick={onEventClick}
        />
      ))}

      {/* Timed events */}
      {timedEvents.map((event) => {
        const { topPx, heightPx } = getEventPosition(
          event.startTime,
          event.endTime,
        );
        const overlap = overlapMap.get(event.id) ?? {
          column: 0,
          totalColumns: 1,
        };
        const isDragging = event.id === draggingEventId;
        const isResizing = event.id === resizingEventId;
        return (
          <EventBlock
            key={event.id}
            event={event}
            topPx={topPx}
            heightPx={heightPx}
            column={overlap.column}
            totalColumns={overlap.totalColumns}
            linkedTaskCount={linkedCountByEvent?.get(event.id) ?? 0}
            isDragging={isDragging}
            isResizing={isResizing}
            overrideHeightPx={isResizing ? resizingHeightPx : undefined}
            overrideTopPx={isResizing ? resizingTopPx : undefined}
            overrideStartMinutes={isResizing ? resizingStartMinutes : undefined}
            overrideEndMinutes={isResizing ? resizingEndMinutes : undefined}
            today={today}
            now={now}
            onClick={onEventClick}
            onLongPress={onEventLongPress}
            onContextMenu={onEventContextMenu}
            onDragStart={onDragStart ?? (() => {})}
            onResizeStart={onResizeStart ?? (() => {})}
          />
        );
      })}

      {nowOffset !== null && <NowIndicator offsetPx={nowOffset} />}

      {/* Resize edge time indicator */}
      {resizingEventId && resizingEdge && resizingTopPx !== undefined && resizingHeightPx !== undefined && (() => {
        const edgeMinutes = resizingEdge === "top" ? resizingStartMinutes : resizingEndMinutes;
        if (edgeMinutes === undefined) return null;
        const edgePx = resizingEdge === "top"
          ? resizingTopPx
          : resizingTopPx + resizingHeightPx;
        const timeLabel = edgeMinutes === DAY_START_HOUR * 60 + 1440
          ? "00:00"
          : minutesToTime(edgeMinutes);
        return (
          <div
            className="pointer-events-none absolute left-0 right-0 z-[60]"
            style={{ top: `${edgePx}px` }}
          >
            <div className="absolute inset-x-0 top-0 border-t border-dashed border-accent/50" />
            <span
              className={`absolute left-1 rounded bg-accent/90 px-1.5 py-0.5 text-[10px] leading-none font-medium text-accent-foreground ${
                resizingEdge === "top" ? "-top-4" : "top-0.5"
              }`}
            >
              {timeLabel}
            </span>
          </div>
        );
      })()}

      {hoverY !== null && hoverTime !== null && !isDraggingOrResizing && !slotCreationPreview && (
        <HoverTimeIndicator hoverY={hoverY} hoverTime={hoverTime} elevated={shiftHeld} />
      )}

      {slotCreationPreview && (() => {
        const ghostOverlap = overlapMap.get("__ghost__" as EventId);
        const col = ghostOverlap?.column ?? 0;
        const total = ghostOverlap?.totalColumns ?? 1;
        return (
          <SlotCreationPreviewBlock
            date={date}
            topPx={slotCreationPreview.topPx}
            heightPx={slotCreationPreview.heightPx}
            column={col}
            totalColumns={total}
          />
        );
      })()}

      {/* Mobile touch "+" indicator */}
      {touchY !== null && !isDraggingOrResizing && !slotCreationPreview && (
        <div
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none md:hidden animate-fade-in"
          style={{ top: `${touchY - 14}px` }}
        >
          <div className="w-7 h-7 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold shadow-lg">
            +
          </div>
        </div>
      )}
    </div>
  );
}
